import { Injectable } from "@angular/core";
import { AplicacionService } from '../../services/aplicacion.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { AppState } from 'src/app/reducers';
import { ToastService } from 'src/app/services/toast.service';
import { AplicacionActionTypes, AllAplicacionRequested, AllAplicacionLoaded, AllAplicacionCancelled, AplicacionToggleMarkedRequested, AplicacionToggleMarkedLoaded, AplicacionToggleMarkedCancelled, AccesoUrlRequested, AccesoUrlLoaded, AccesoUrlCancelled, MFTokenPersonaRequested, MFTokenPersonaLoaded, MFTokenPersonaCancelled, AccesoDateRequested, AccesoDateLoaded, AccesoDateCancelled, PortalAuditorTokenLoaded, PortalAuditorTokenCancelled, IpClienteRequested, IpClienteCancelled, IpClienteLoaded, HCETokenRequested, HCETokenLoaded, HCETokenCancelled, AllAplicacionRight, CredTokenRequested, CredTokenCancelled, CredTokenLoaded } from './aplicacion.actions';
import { withLatestFrom, switchMap, catchError, map } from 'rxjs/operators';
import { sessionConnectionType, otherSystemData } from 'src/app/session/store/session.selectors';
import { Observable, of } from 'rxjs';
import { AccesoAplicacion, AccesoAplicacionMarkable, AccesoPerfil, MFTokenPersona } from 'src/common/acceso';
import { aplicacionAccesoIpCliente, aplicacionAll } from './aplicacion.selectors';


@Injectable()
export class AplicacionEffects {

	private readonly IP_CLIENTE_KEY: string = "client-IP";

	loadAcceso$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<AllAplicacionRequested>( AplicacionActionTypes.AllAplicacionRequested ),
			withLatestFrom( this.store.select( aplicacionAccesoIpCliente ) ),
			switchMap( ([ action, localIpCliente ]) => {
				return !localIpCliente ? 
					this.service.getIpCliente().pipe(
						map( remoteIpCliente => {
							return { action, remoteIpCliente: remoteIpCliente[ this.IP_CLIENTE_KEY ] };
						}),
						catchError( error => of( { action, remoteIpCliente: null } ) )
					) : of( { action, remoteIpCliente: localIpCliente } );
			}),
			withLatestFrom( this.store.select( sessionConnectionType ), this.store.select( aplicacionAll ) ),
			switchMap( ([ data, connectionType, accesos ]) => {
				let resultAction: Observable<Action>;
				let forceReload: boolean = data.action.payload.forceReload;
				let remoteIpCliente: string = data.remoteIpCliente;
				
				// Si no hay accesos, es la primera vez que se carga el componenete
				if( accesos.length === 0 ) {
					forceReload = true;
				}

				if( connectionType == null ) {
					this.toastService.danger( "No se pudo obtener el tipo de conexión" );
					resultAction = of( new AllAplicacionCancelled() );
				} else if( remoteIpCliente == null ) {
					this.toastService.danger( "No se pudo obtener la dirección IP" );
					resultAction = of( new AllAplicacionCancelled() );
				} else if( forceReload ) {
					resultAction = this.service.findAplicaciones( connectionType, remoteIpCliente ).pipe(
						map( ( accesos: AccesoAplicacionMarkable[] ) =>
							new AllAplicacionLoaded( { accesos, ipCliente: remoteIpCliente } )
						),
						catchError( error => {
							this.toastService.danger( "No se pudieron obtener los accesos a las aplicaciones" );
							return of( new AllAplicacionCancelled() );
						})
					);
				} else {
					resultAction = of( new AllAplicacionRight() );
				}

				return resultAction;
			} )
		)
	});

	toggleMarked$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<AplicacionToggleMarkedRequested>( AplicacionActionTypes.AplicacionToggleMarkedRequested ),
			switchMap( action => {
				let acceso: AccesoAplicacionMarkable = action.payload;
				let observable: Observable<Object> = acceso.marked? this.service.removeAplicacionFavorita( acceso.id ): this.service.addAplicacionFavorita( acceso.id, acceso.nombre );
				return observable
					.pipe(
						map( () => new AplicacionToggleMarkedLoaded( acceso.id ) ),
						catchError( error => {
							this.toastService.danger( "La operación no pudo realizarse" );
							return of( new AplicacionToggleMarkedCancelled( acceso.id ) );
						})
					)
			})
		);
	});

/**
 * Acceso related effects
 */

	accesoUrl$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<AccesoUrlRequested>( AplicacionActionTypes.AccesoUrlRequested ),
			withLatestFrom( this.store.select( aplicacionAll ) ),
			map( ([ action, accesos ]) => {
				let aplicacion: AccesoAplicacion = accesos.find( aplicacion => aplicacion.nombre == action.payload.ida );
				let perfil: AccesoPerfil = aplicacion? aplicacion.perfiles.find( perfil => perfil.nombre == action.payload.idp ): null;
				return perfil && perfil.url? new AccesoUrlLoaded( { url: perfil.url } ): new AccesoUrlCancelled();
			})
		);
	});

	mfPersonaToken$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<MFTokenPersonaRequested>( AplicacionActionTypes.MFTokenPersonaRequested ),
			withLatestFrom( this.store.select( aplicacionAll ) ),
			switchMap( ([ action, accesos ]) => {
				let ida: string = action.payload.nombreAplicacion;
				let idp: string = action.payload.nombreAplicacionPerfil;

				// Busco el id de la aplicación perfil a partir del nombre de la aplicación perfil
				let accesoAplicacion: AccesoAplicacion = accesos.find( acceso => acceso.nombre == ida );
				let accesoPerfil: AccesoPerfil = accesoAplicacion? accesoAplicacion.perfiles.find( acceso => acceso.nombre == idp ): null;
				let observable: Observable<MFTokenPersona> = accesoPerfil?
					accesoAplicacion? this.service.createMFPersonaToken( accesoPerfil.id ): of( null ):
					of( null );

				return observable
					.pipe(
						catchError( error => of( null ) )
					);
			}),
			map( mfTokenPersona => mfTokenPersona? new MFTokenPersonaLoaded( mfTokenPersona ): new MFTokenPersonaCancelled() ),
			catchError( error => of( new MFTokenPersonaCancelled() ) )
		);
	});

	accesoDate$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<AccesoDateRequested>( AplicacionActionTypes.AccesoDateRequested ),
			switchMap( action => {
				return this.service.getAccesoDate()
					.pipe(
						catchError( error => of( null ) )
					);
			}),
			map( accesoDate => accesoDate? new AccesoDateLoaded( accesoDate ): new AccesoDateCancelled() ),
			catchError( error => of( new AccesoDateCancelled() ) )
		);
	});

	portalAuditorToken$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<MFTokenPersonaRequested>( AplicacionActionTypes.PortalAuditorTokenRequested ),
			withLatestFrom( this.store.select( otherSystemData ) ),
			switchMap( ([ action, data ]) => {
				return data == null || data.idPersonaFederada == null? of( null ):
					this.service.createPortalAuditorToken( data.idPersonaFederada )
						.pipe(
							catchError( error => of( null ) )
						);
			}),
			map( portalAuditorToken => portalAuditorToken? new PortalAuditorTokenLoaded( portalAuditorToken ): new PortalAuditorTokenCancelled() ),
			catchError( error => of( new PortalAuditorTokenCancelled() ) )
		);
	});

	ipCliente$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<IpClienteRequested>( AplicacionActionTypes.IpClienteRequested ),
			withLatestFrom( this.store.select( aplicacionAccesoIpCliente ) ),
			switchMap( ([ action, ipCliente ]) => {
				return ipCliente == null?
					this.service.getIpCliente()
						.pipe(
							map( ipCliente => ipCliente[ this.IP_CLIENTE_KEY ] ),
							catchError( error => of(null) )
						):
					of( ipCliente );
			}),
			map( (ipCliente: string) => ipCliente == null? new IpClienteCancelled(): new IpClienteLoaded( { ipCliente } ) )
		);
	});

	hceToken$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<HCETokenRequested>( AplicacionActionTypes.HCETokenRequested ),
			switchMap( action => {
				return this.service.createHCEToken()
					.pipe(
						catchError( error => of( null ) )
					)
			}),
			map( hceToken => hceToken? new HCETokenLoaded( hceToken ): new HCETokenCancelled() ),
			catchError( error => of( new HCETokenCancelled() ) )
		);
	});

	credToken$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<CredTokenRequested>( AplicacionActionTypes.CredTokenRequested ),
			switchMap( action => {
				return this.service.createCredToken( action.payload.ne, action.payload.ce )
					.pipe(
						catchError( error => of( null ) )
					)
			}),
			map( credToken => credToken? new CredTokenLoaded( credToken ): new CredTokenCancelled() ),
			catchError( error => of( new CredTokenCancelled() ) )
		);
	});


    constructor( private actions$: Actions, private store: Store<AppState>, private service: AplicacionService, private toastService: ToastService ) {
    }

}